import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FiChevronLeft, FiChevronRight, FiCloud, FiFolder, FiHardDrive, FiInbox, FiPlus, FiSearch, FiUploadCloud, FiZap } from "react-icons/fi";
import { client } from "../../Clients";
import type { StorageObject } from "../../API";
import type { MediaOrphanReportDto } from "../Checks/checksCommon";
import GatedButton from "../GatedButton";
import { LoadingSpinner } from "../LoadingSpinner";
import { Modal } from "../Modal";
import type { IApplicationState } from "../../Redux/Reducers";

import type { MediaAccept, MediaScope, MediaTab, SortDir, SortKey, UsageScanState, ViewMode } from "./types";
import { BROWSE_PAGE_SIZE, MEDIA_TABS, ROOT_PATHS, ROOT_PLACEHOLDER, VIEW_MODE_STORAGE_KEY } from "./types";
import { extractErrorMessage, formatFileSize, resolveFileKind, sortFolders, sortStorageObjects, sumSizes } from "./utils";
import { EmptyState, FileGrid, FileTable, FolderCard, GridSkeleton, TableSkeleton } from "./BrowserView";
import { BulkBar } from "./BulkBar";
import { DetailPanel } from "./DetailPanel";
import { FolderTree } from "./FolderTree";
import { MediaToolbar } from "./MediaToolbar";
import { OrphansView } from "./OrphansView";
import { UploadModal } from "./UploadModal";
import { useLocalMediaNav } from "./useLocalMediaNav";
import { useMediaLibraryData } from "./useMediaLibraryData";
import type { StorageUrlSync } from "./useStorageUrlSync";
import { useStorageUrlSync } from "./useStorageUrlSync";

const WRITE_TOKEN = "feature:storage:write";

export interface MediaLibraryProps {
	/**
	 * « manage » : la Médiathèque complète (URL synchronisée, panneau de détail, upload, actions
	 * bulk, vue orphelins). « pick » : sélecteur embarquable : navigation en état local (l'URL de
	 * la page hôte n'est pas touchée), cliquer un fichier appelle onPick.
	 */
	mode: "manage" | "pick";
	onPick?: (object: StorageObject) => void;
	/** Restriction de type : pré-filtre onglets, dossiers racine et fichiers listés. */
	accept?: MediaAccept;
	/** Incrémenté par l'hôte pour forcer un rechargement (ex. après un upload externe). */
	refreshKey?: number;
	/** Dossier d'ouverture (mode pick) : ex. un champ épinglé sur « publications/rapports ». */
	initialPath?: string;
}

/**
 * Médiathèque du bucket LIVE (source de vérité des médias). La racine montre directement TOUS
 * les médias (onglets Tous / Images / Documents / Logo, recherche globale, filtre taxonomies,
 * tri) ; la navigation par dossiers reste en second rideau (rail FolderTree, cartes sur petits
 * écrans, fil d'ariane). Pipeline de données unique (useMediaLibraryData) sur GET /api/storage
 * et GET /api/storage/browse. En mode manage, tout l'état navigable vit dans l'URL (?path,
 * ?file, ?q, ?tab, ?tax) via useStorageUrlSync ; la sélection multiple alimente la BulkBar
 * (recompression / suppression en série) et la vue « Orphelins » réutilise le rapport du scan
 * (POST /api/storage/orphans/scan), lancé au plus UNE fois puis mis en cache. Toutes les
 * écritures passent par GatedButton feature:storage:write.
 */
export const MediaLibrary: React.FC<MediaLibraryProps> = (props) =>
	props.mode === "manage" ? <ManagedMediaLibrary {...props} /> : <PickMediaLibrary {...props} />;

/** Mode manage : la navigation vit dans l'URL de la page (deep-links, Précédent/Suivant). */
const ManagedMediaLibrary: React.FC<MediaLibraryProps> = (props) => {
	const nav = useStorageUrlSync();
	return <MediaLibraryView {...props} nav={nav} />;
};

/** Mode pick : navigation en état local : l'URL de la page hôte n'est JAMAIS touchée. */
const PickMediaLibrary: React.FC<MediaLibraryProps> = (props) => {
	const nav = useLocalMediaNav(props.initialPath);
	return <MediaLibraryView {...props} nav={nav} />;
};

const MediaLibraryView: React.FC<MediaLibraryProps & { nav: StorageUrlSync }> = ({
	mode,
	onPick,
	accept = "all",
	refreshKey = 0,
	nav
}) => {
	const isManage = mode === "manage";
	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);

	const { urlState, navigateToPath, setTab, setSearch, setTaxonomyIds, selectFile, showUploaded } = nav;
	const { path: currentPath, fileId, q, tab: rawTab, taxonomyIds: activeTaxonomyIds } = urlState;
	const isRootView = currentPath === ROOT_PLACEHOLDER;
	const scope: MediaScope = isRootView ? "all" : "folder";
	const taxonomyKey = activeTaxonomyIds.join(",");

	// ---- Restriction de type (`accept`) : onglets réduits + onglet effectif contraint ------------
	const tabs = useMemo(() => {
		if (accept === "images") return MEDIA_TABS.filter((item) => item.id === "images" || item.id === "logo");
		if (accept === "documents") return MEDIA_TABS.filter((item) => item.id === "documents");
		return MEDIA_TABS;
	}, [accept]);
	const tab: MediaTab = tabs.some((item) => item.id === rawTab) ? rawTab : tabs[0].id;

	const taxonomyNameById = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomy) => {
			if (taxonomy.id) map.set(taxonomy.id, taxonomy.title ?? taxonomy.id);
		});
		return map;
	}, [taxonomies]);

	// ---- Vue -----------------------------------------------------------------------------------
	const [viewMode, setViewMode] = useState<ViewMode>(() => {
		try {
			const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
			return stored === "list" ? "list" : "grid";
		} catch {
			return "grid";
		}
	});
	const [sortKey, setSortKey] = useState<SortKey>("date");
	const [sortDir, setSortDir] = useState<SortDir>("desc");
	const [orphansMode, setOrphansMode] = useState(false);

	// ---- Données (pipeline unique) ---------------------------------------------------------------
	const [page, setPage] = useState(1);
	const [refreshToken, setRefreshToken] = useState(0);
	useEffect(() => {
		setPage(1);
	}, [currentPath, tab, q, taxonomyKey]);

	const media = useMediaLibraryData({
		scope,
		path: isRootView ? undefined : currentPath,
		tab,
		search: q,
		taxonomyIds: activeTaxonomyIds,
		page,
		refreshToken: refreshToken + refreshKey
	});
	const { patchFile, dropFile } = media;

	// ---- Recherche (champ local débouncé → nav.setSearch) -----------------------------------------
	const [searchInput, setSearchInput] = useState(q);
	// Dernière valeur écrite par le débounce : distingue nos écritures d'un Précédent/Suivant.
	const lastAppliedSearch = useRef(q);
	useEffect(() => {
		if (q !== lastAppliedSearch.current) {
			lastAppliedSearch.current = q;
			setSearchInput(q);
		}
	}, [q]);
	useEffect(() => {
		const handle = window.setTimeout(() => {
			const trimmed = searchInput.trim();
			if (trimmed === lastAppliedSearch.current) return;
			lastAppliedSearch.current = trimmed;
			setSearch(trimmed);
		}, 300);
		return () => window.clearTimeout(handle);
	}, [searchInput, setSearch]);

	// ---- Sélection simple (panneau de détail, mode manage) ----------------------------------------
	const [selected, setSelected] = useState<StorageObject | null>(null);
	const selectedRef = useRef<StorageObject | null>(null);
	useEffect(() => {
		selectedRef.current = selected;
	}, [selected]);

	const selectObject = useCallback(
		(object: StorageObject | null) => {
			if (!isManage) {
				if (object) onPick?.(object);
				return;
			}
			setSelected(object);
			selectFile(object?.id ?? null);
		},
		[isManage, onPick, selectFile]
	);

	// Lien profond ?file= : restaure la sélection au chargement (ou après Précédent/Suivant).
	useEffect(() => {
		if (!isManage) return;
		if (!fileId) {
			setSelected(null);
			return;
		}
		if (selectedRef.current?.id === fileId) return;
		let cancelled = false;
		client.storage
			.apiStorageIdGet(fileId)
			.then((response) => {
				if (!cancelled && response.data) setSelected(response.data);
			})
			.catch(() => {
				/* fichier supprimé/introuvable : on laisse simplement le panneau fermé */
			});
		return () => {
			cancelled = true;
		};
	}, [isManage, fileId]);

	// ---- Sélection multiple (BulkBar, mode manage) -------------------------------------------------
	const [bulkMap, setBulkMap] = useState<Map<string, StorageObject>>(() => new Map());
	const [bulkBusy, setBulkBusy] = useState(false);
	const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
	useEffect(() => {
		setBulkMap(new Map());
	}, [currentPath, tab, q, taxonomyKey]);

	const toggleBulk = useCallback((object: StorageObject) => {
		const id = object.id;
		if (!id) return;
		setBulkMap((prev) => {
			const next = new Map(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.set(id, object);
			}
			return next;
		});
	}, []);

	const bulkObjects = useMemo(() => Array.from(bulkMap.values()), [bulkMap]);
	const bulkImages = useMemo(() => bulkObjects.filter((object) => resolveFileKind(object) === "image"), [bulkObjects]);
	const bulkIds = useMemo(() => new Set(bulkMap.keys()), [bulkMap]);

	// ---- Upload ------------------------------------------------------------------------------------
	const [uploadOpen, setUploadOpen] = useState(false);
	const [droppedFile, setDroppedFile] = useState<File | null>(null);
	const [pageDragActive, setPageDragActive] = useState(false);
	const dragDepth = useRef(0);

	// ---- Scan « Utilisé par » (une fois, mis en cache) ---------------------------------------------
	const [usage, setUsage] = useState<UsageScanState>({ report: null, loading: false, error: null });

	const runUsageScan = useCallback(() => {
		setUsage((prev) => {
			if (prev.loading) return prev;
			return { ...prev, loading: true, error: null };
		});
		axios
			.post("/api/storage/orphans/scan", null, { withCredentials: true, headers: { Accept: "application/json" } })
			.then((response) => {
				setUsage({ report: (response.data ?? null) as MediaOrphanReportDto | null, loading: false, error: null });
			})
			.catch((error) => {
				console.error("Échec du scan des médias orphelins", error);
				setUsage({ report: null, loading: false, error: extractErrorMessage(error) ?? "Échec de l'analyse d'utilisation." });
			});
	}, []);

	// ---- Actions bulk ------------------------------------------------------------------------------
	const runBulkRecompress = useCallback(async () => {
		if (bulkBusy || !bulkImages.length) return;
		setBulkBusy(true);
		let done = 0;
		let failed = 0;
		// En série : la recompression est coûteuse côté API, on ne la parallélise pas.
		for (const image of bulkImages) {
			try {
				const response = await client.storage.apiStorageIdRecompressPost(image.id!);
				if (response.data) patchFile(response.data);
				done += 1;
			} catch (error) {
				console.error("Échec de la recompression bulk", error);
				failed += 1;
			}
		}
		setBulkBusy(false);
		if (failed) {
			toast.warn(`${done} image${done > 1 ? "s" : ""} recompressée${done > 1 ? "s" : ""}, ${failed} échec${failed > 1 ? "s" : ""}.`);
		} else {
			toast.success(`${done} image${done > 1 ? "s" : ""} recompressée${done > 1 ? "s" : ""}.`);
		}
	}, [bulkBusy, bulkImages, patchFile]);

	const runBulkDelete = useCallback(async () => {
		if (bulkBusy || !bulkObjects.length) return;
		setConfirmBulkDelete(false);
		setBulkBusy(true);
		let done = 0;
		let failed = 0;
		for (const object of bulkObjects) {
			try {
				await client.storage.apiStorageIdDelete(object.id!);
				dropFile(object.id!);
				done += 1;
			} catch (error) {
				console.error("Échec de la suppression bulk", error);
				failed += 1;
			}
		}
		if (selectedRef.current?.id && bulkMap.has(selectedRef.current.id)) {
			selectObject(null);
		}
		setBulkMap(new Map());
		// Le rapport d'utilisation référence encore les fichiers supprimés : on l'invalide.
		setUsage({ report: null, loading: false, error: null });
		setRefreshToken((token) => token + 1);
		setBulkBusy(false);
		if (failed) {
			toast.warn(`${done} fichier${done > 1 ? "s" : ""} supprimé${done > 1 ? "s" : ""}, ${failed} échec${failed > 1 ? "s" : ""}.`);
		} else {
			toast.success(`${done} fichier${done > 1 ? "s" : ""} supprimé${done > 1 ? "s" : ""} du stockage.`);
		}
	}, [bulkBusy, bulkObjects, bulkMap, dropFile, selectObject]);

	// ---- Recompression globale (POST /api/storage/recompress-all) ---------------------------------
	const [confirmRecompressAll, setConfirmRecompressAll] = useState(false);
	const [recompressAllBusy, setRecompressAllBusy] = useState(false);
	const runRecompressAll = useCallback(async () => {
		setConfirmRecompressAll(false);
		setRecompressAllBusy(true);
		try {
			const response = await client.storage.apiStorageRecompressAllPost();
			const result = response.data;
			toast.success(
				`Recompression terminée : ${result?.compressed ?? 0} recompressée(s), ${result?.skipped ?? 0} ignorée(s), ${result?.failed ?? 0} échec(s) sur ${result?.total ?? 0}.`
			);
			setRefreshToken((token) => token + 1);
		} catch (error) {
			console.error("Échec de la recompression globale", error);
			toast.error(extractErrorMessage(error) ?? "La recompression globale a échoué.");
		} finally {
			setRecompressAllBusy(false);
		}
	}, []);

	// ---- Bascule grille/liste persistée -----------------------------------------------------------
	const switchViewMode = useCallback((next: ViewMode) => {
		setViewMode(next);
		try {
			window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, next);
		} catch {
			/* stockage local indisponible (navigation privée) : la préférence ne survivra pas, sans plus */
		}
	}, []);

	const handleSortChange = useCallback((key: SortKey, dir: SortDir) => {
		setSortKey(key);
		setSortDir(dir);
	}, []);

	const handleSortHeader = useCallback(
		(key: SortKey) => {
			if (key === sortKey) {
				setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
			} else {
				setSortKey(key);
				setSortDir(key === "name" ? "asc" : "desc"); // taille/date : le plus gros / le plus récent d'abord
			}
		},
		[sortKey]
	);

	const handleRefresh = useCallback(() => setRefreshToken((token) => token + 1), []);

	// Naviguer (dossier / onglet) quitte la vue orphelins : elle est globale, pas par dossier.
	const navigate = useCallback(
		(path: string, options?: { keepSearch?: boolean }) => {
			setOrphansMode(false);
			navigateToPath(path, options);
		},
		[navigateToPath]
	);
	const changeTab = useCallback(
		(next: Parameters<typeof setTab>[0]) => {
			setOrphansMode(false);
			setTab(next);
		},
		[setTab]
	);

	// ---- Retour clavier : Backspace (hors champ de saisie) remonte d'un dossier ---------------------
	const parentPath = useMemo(() => {
		if (isRootView) return null;
		const crumbs = media.breadcrumbs;
		if (crumbs.length > 1) return crumbs[crumbs.length - 2]?.path ?? ROOT_PLACEHOLDER;
		const segments = currentPath.split("/");
		return segments.length > 1 ? segments.slice(0, -1).join("/") : ROOT_PLACEHOLDER;
	}, [isRootView, media.breadcrumbs, currentPath]);

	useEffect(() => {
		if (!isManage) return;
		const handler = (event: KeyboardEvent) => {
			if (event.key !== "Backspace" || isRootView || uploadOpen) return;
			const target = event.target as HTMLElement | null;
			const tag = (target?.tagName ?? "").toLowerCase();
			if (tag === "input" || tag === "textarea" || tag === "select" || target?.isContentEditable) return;
			event.preventDefault();
			if (parentPath) navigate(parentPath);
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [isManage, isRootView, parentPath, navigate, uploadOpen]);

	// ---- Glisser-déposer sur toute la zone de contenu → ouvre la modale d'ajout pré-remplie ---------
	const onPageDrop = (event: React.DragEvent) => {
		event.preventDefault();
		dragDepth.current = 0;
		setPageDragActive(false);
		if (uploadOpen) return; // la modale gère elle-même sa zone de dépôt
		const file = event.dataTransfer?.files?.[0];
		if (file) {
			setDroppedFile(file);
			setUploadOpen(true);
		}
	};

	// ---- Données affichées ---------------------------------------------------------------------------
	const folders = useMemo(() => sortFolders(media.folders.filter((child) => Boolean(child?.path))), [media.folders]);
	// Filet de sécurité client de la restriction `accept` : le serveur filtre par dossier racine,
	// mais un dossier peut contenir des types mélangés (anciens uploads).
	const acceptedFiles = useMemo(() => {
		if (accept === "all") return media.files;
		return media.files.filter((file) => {
			const kind = resolveFileKind(file);
			return accept === "images" ? kind === "image" : kind === "pdf" || kind === "document";
		});
	}, [accept, media.files]);
	const files = useMemo(() => sortStorageObjects(acceptedFiles, sortKey, sortDir), [acceptedFiles, sortKey, sortDir]);

	const loadedBytes = useMemo(() => sumSizes(media.files), [media.files]);
	const sizePartial = media.hasMore || page > 1;
	const fileCountLabel = useMemo(() => {
		if (media.totalFiles !== null) return media.totalFiles.toLocaleString("fr-FR");
		const count = (page - 1) * BROWSE_PAGE_SIZE + media.files.length;
		return `${media.hasMore ? "≥ " : ""}${count.toLocaleString("fr-FR")}`;
	}, [media.totalFiles, media.hasMore, media.files.length, page]);

	const rootFolders = useMemo(
		() =>
			ROOT_PATHS.filter((root) =>
				accept === "all" ? true : accept === "images" ? root.kind === "image" : root.kind === "publication"
			).map((root) => ({
				id: root.path,
				name: root.label,
				path: root.path,
				parentPath: null,
				kind: root.kind,
				depth: 0
			})),
		[accept]
	);

	// ---- Callbacks du panneau de détail ---------------------------------------------------------------
	const handleObjectUpdated = useCallback(
		(updated: StorageObject) => {
			setSelected(updated);
			patchFile(updated);
		},
		[patchFile]
	);

	const handleDeleted = useCallback(
		(object: StorageObject) => {
			selectObject(null);
			if (object.id) {
				dropFile(object.id);
				setBulkMap((prev) => {
					if (!prev.has(object.id!)) return prev;
					const next = new Map(prev);
					next.delete(object.id!);
					return next;
				});
			}
			setUsage({ report: null, loading: false, error: null });
			setRefreshToken((token) => token + 1);
		},
		[selectObject, dropFile]
	);

	const handleUploaded = useCallback(
		async (object: StorageObject) => {
			setUploadOpen(false);
			setDroppedFile(null);
			setUsage({ report: null, loading: false, error: null });
			// La réponse d'upload revient avec un id VIDE (seul le magasin de documents le pose) :
			// on résout la ligne par sa CLÉ naturelle pour pouvoir sélectionner le nouveau fichier.
			let resolved = object;
			if (!resolved.id && resolved.key) {
				try {
					const byKey = await client.storage.apiStorageByKeyGet(resolved.key);
					if (byKey.data) resolved = byKey.data;
				} catch {
					/* la sélection est un confort : le dossier rafraîchi montre le fichier de toute façon */
				}
			}
			setOrphansMode(false);
			// Ouvre le dossier du fichier ajouté ET le sélectionne : un seul push d'historique.
			showUploaded(resolved.folder ?? null, resolved.id ?? null);
			setSelected(resolved.id ? resolved : null);
			setRefreshToken((token) => token + 1);
		},
		[showUploaded]
	);

	const totalPages = media.totalFiles !== null && media.totalFiles > 0 ? Math.max(1, Math.ceil(media.totalFiles / BROWSE_PAGE_SIZE)) : null;
	const showPagination = media.hasMore || page > 1;
	const hasActiveFilter = q.length > 0 || activeTaxonomyIds.length > 0;
	// Cartes de dossiers à la racine : sur l'onglet par défaut seulement (avec `accept`, l'onglet
	// par défaut n'est plus « Tous » mais la première catégorie autorisée).
	const showRootFolderCards = isRootView && tab === tabs[0].id && !hasActiveFilter;
	const showFolderCards = !isRootView && viewMode === "grid" && folders.length > 0;
	// Sur grand écran le rail FolderTree remplace les cartes de dossiers (mode manage).
	const folderCardsClass = isManage ? "lg:hidden" : "";
	const showDetail = isManage && Boolean(selected);

	const toolbar = (
		<MediaToolbar
			mode={mode}
			isRootView={isRootView}
			tab={tab}
			onTabChange={changeTab}
			breadcrumbs={media.breadcrumbs}
			currentPath={currentPath}
			onNavigate={navigate}
			searchValue={searchInput}
			onSearchChange={setSearchInput}
			taxonomies={taxonomies}
			taxonomyIds={activeTaxonomyIds}
			onTaxonomyChange={setTaxonomyIds}
			sortKey={sortKey}
			sortDir={sortDir}
			onSortChange={handleSortChange}
			loading={media.loading}
			onRefresh={handleRefresh}
			viewMode={viewMode}
			onViewModeChange={switchViewMode}
			orphansMode={orphansMode}
			onToggleOrphans={isManage ? () => setOrphansMode((current) => !current) : undefined}
			tabs={tabs}
		/>
	);

	const browserContent = (
		<>
			{/* Racine : navigation par dossiers en second rideau (cartes : le rail les couvre en lg+). */}
			{showRootFolderCards && (
				<div className={folderCardsClass}>
					<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Parcourir par dossier</p>
					<div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
						{rootFolders.map((folder) => (
							<FolderCard
								key={folder.path}
								folder={folder}
								onOpen={navigate}
								subtitle={folder.kind === "publication" ? "Documents et rapports" : "Ressources visuelles"}
							/>
						))}
					</div>
				</div>
			)}

			{/* Dossier : sous-dossiers en cartes (vue grille : la vue liste les intègre au tableau). */}
			{showFolderCards && (
				<div className={`grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3 ${folderCardsClass}`}>
					{folders.map((folder) => (
						<FolderCard key={folder.id ?? folder.path ?? folder.name ?? ""} folder={folder} onOpen={navigate} subtitle={folder.path} />
					))}
				</div>
			)}

			{/* Fichiers */}
			{media.loading && !files.length ? (
				viewMode === "grid" ? (
					<GridSkeleton />
				) : (
					<TableSkeleton />
				)
			) : files.length === 0 && (isRootView || folders.length === 0 || viewMode === "grid") ? (
				q ? (
					<EmptyState
						icon={<FiSearch aria-hidden />}
						title={`Aucun fichier ne correspond à « ${q} ».`}
						hint={isRootView ? "La recherche couvre tous les médias (titre, nom de fichier, clé)." : "La recherche est limitée à ce dossier."}
					>
						{!isRootView && (
							<button
								type="button"
								onClick={() => navigate(ROOT_PLACEHOLDER, { keepSearch: true })}
								className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
							>
								<FiSearch className="h-4 w-4" aria-hidden /> Chercher dans tous les médias
							</button>
						)}
					</EmptyState>
				) : activeTaxonomyIds.length > 0 ? (
					<EmptyState icon={<FiSearch aria-hidden />} title="Aucun fichier ne porte les taxonomies sélectionnées." hint="Élargissez ou effacez le filtre pour revoir les fichiers.">
						<button
							type="button"
							onClick={() => setTaxonomyIds([])}
							className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
						>
							Effacer le filtre
						</button>
					</EmptyState>
				) : files.length === 0 && (isRootView || folders.length === 0) ? (
					<EmptyState
						icon={<FiInbox aria-hidden />}
						title={
							!isRootView
								? "Ce dossier ne contient encore aucun fichier."
								: tab === "all"
								? "Aucun média dans le stockage."
								: "Aucun média dans cette catégorie."
						}
						hint={isManage ? "Glissez-déposez un fichier n'importe où sur la page pour l'ajouter." : undefined}
					>
						{isManage && (
							<GatedButton
								token={WRITE_TOKEN}
								onClick={() => {
									setDroppedFile(null);
									setUploadOpen(true);
								}}
								className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
							>
								<FiUploadCloud className="h-4 w-4" /> Ajouter un fichier
							</GatedButton>
						)}
					</EmptyState>
				) : null
			) : files.length > 0 || (viewMode === "list" && folders.length > 0) ? (
				viewMode === "grid" ? (
					<FileGrid
						objects={files}
						selectedId={selected?.id ?? null}
						onSelect={selectObject}
						showFolder={isRootView}
						taxonomyNameById={taxonomyNameById}
						bulkSelectedIds={isManage ? bulkIds : undefined}
						onToggleBulk={isManage ? toggleBulk : undefined}
					/>
				) : (
					<FileTable
						folders={isRootView ? [] : folders}
						objects={files}
						selectedId={selected?.id ?? null}
						sortKey={sortKey}
						sortDir={sortDir}
						onSort={handleSortHeader}
						onOpenFolder={navigate}
						onSelect={selectObject}
						showFolder={isRootView}
						taxonomyNameById={taxonomyNameById}
						bulkSelectedIds={isManage ? bulkIds : undefined}
						onToggleBulk={isManage ? toggleBulk : undefined}
					/>
				)
			) : null}

			{/* Pagination */}
			{showPagination && (
				<div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm">
					<button
						type="button"
						onClick={() => setPage((current: number) => Math.max(1, current - 1))}
						disabled={page === 1 || media.loading}
						className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<FiChevronLeft className="h-3.5 w-3.5" /> Précédente
					</button>
					<span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
						Page {page}
						{totalPages ? ` sur ${totalPages}` : ""}
					</span>
					<button
						type="button"
						onClick={() => media.hasMore && setPage((current: number) => current + 1)}
						disabled={!media.hasMore || media.loading}
						className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						Suivante <FiChevronRight className="h-3.5 w-3.5" />
					</button>
				</div>
			)}

			{/* Barre d'actions sur la sélection multiple */}
			{isManage && (
				<BulkBar
					count={bulkMap.size}
					imageCount={bulkImages.length}
					busy={bulkBusy}
					onRecompress={runBulkRecompress}
					onDelete={() => setConfirmBulkDelete(true)}
					onClear={() => setBulkMap(new Map())}
				/>
			)}
		</>
	);

	return (
		<div
			className={isManage ? "min-h-full p-6" : ""}
			onDragEnter={
				isManage
					? (e) => {
							if (!uploadOpen && e.dataTransfer?.types?.includes("Files")) {
								dragDepth.current += 1;
								setPageDragActive(true);
							}
					  }
					: undefined
			}
			onDragLeave={
				isManage
					? () => {
							dragDepth.current = Math.max(0, dragDepth.current - 1);
							if (dragDepth.current === 0) setPageDragActive(false);
					  }
					: undefined
			}
			onDragOver={isManage ? (e) => e.preventDefault() : undefined}
			onDrop={isManage ? onPageDrop : undefined}
		>
			<div className={isManage ? "mx-auto w-full max-w-[1600px] space-y-4" : "space-y-3"}>
				{isManage ? (
					<header className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
						<div className="flex flex-wrap items-center gap-3">
							<div className="min-w-0">
								<p className="text-xs uppercase tracking-widest text-slate-500">Médiathèque</p>
								<h1 className="text-2xl font-semibold text-slate-900">Stockage</h1>
								<p className="text-sm text-slate-500">
									Les images et publications du site : la source que les déploiements copient vers les buckets publics.
								</p>
							</div>
							<div className="ml-auto flex flex-wrap items-center gap-2">
								{!isRootView && (
									<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-600">
										<FiFolder className="h-3.5 w-3.5" aria-hidden />
										{folders.length} dossier{folders.length > 1 ? "s" : ""}
									</span>
								)}
								<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-600">
									<FiCloud className="h-3.5 w-3.5" aria-hidden />
									{fileCountLabel} fichier{fileCountLabel === "1" ? "" : "s"}
								</span>
								<span
									className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-600"
									title={sizePartial ? "Taille des fichiers de la page affichée" : "Taille totale"}
								>
									<FiHardDrive className="h-3.5 w-3.5" aria-hidden />
									{sizePartial ? "≥ " : ""}
									{formatFileSize(loadedBytes)}
								</span>
								<GatedButton
									token={WRITE_TOKEN}
									onClick={() => setConfirmRecompressAll(true)}
									disabled={recompressAllBusy}
									title="Régénère la copie compressée de TOUTES les images du stockage"
									className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
								>
									{recompressAllBusy ? <LoadingSpinner size="xs" inline /> : <FiZap className="h-4 w-4" />} Recompresser les images
								</GatedButton>
								<GatedButton
									token={WRITE_TOKEN}
									onClick={() => {
										setDroppedFile(null);
										setUploadOpen(true);
									}}
									className="inline-flex items-center gap-1.5 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004a87]"
								>
									<FiPlus /> Ajouter un fichier
								</GatedButton>
							</div>
						</div>
						<div className="mt-4 border-t border-slate-100 pt-3">{toolbar}</div>
					</header>
				) : (
					<div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">{toolbar}</div>
				)}

				{media.error && (
					<p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
						{media.error}
						<button
							type="button"
							onClick={() => navigate(ROOT_PLACEHOLDER)}
							className="ml-3 font-semibold underline decoration-rose-400 underline-offset-2"
						>
							Revenir à tous les médias
						</button>
					</p>
				)}

				{/* ---- Zone de contenu : rail dossiers + navigateur + panneau de détail ---- */}
				<div className={`grid items-start gap-4 ${showDetail ? "xl:grid-cols-[minmax(0,1fr)_400px]" : ""}`}>
					<div className="flex items-start gap-4">
						{isManage && !orphansMode && (
							<div className="hidden lg:block">
								<FolderTree currentPath={currentPath} breadcrumbs={media.breadcrumbs} childFolders={media.folders} onNavigate={navigate} />
							</div>
						)}
						<section className="min-w-0 flex-1 space-y-4">
							{orphansMode ? <OrphansView usage={usage} onRequestScan={runUsageScan} onOpen={(id) => selectFile(id)} /> : browserContent}
						</section>
					</div>

					{/* ---- Panneau de détail (colonne collante) ---- */}
					{showDetail && selected && (
						<div className="xl:sticky xl:top-4">
							<DetailPanel
								object={selected}
								taxonomies={taxonomies}
								usage={usage}
								onRequestScan={runUsageScan}
								onClose={() => selectObject(null)}
								onObjectUpdated={handleObjectUpdated}
								onDeleted={handleDeleted}
							/>
						</div>
					)}
				</div>
			</div>

			{/* ---- Voile de dépôt plein écran ---- */}
			{isManage && pageDragActive && (
				<div className="pointer-events-none fixed inset-0 z-[1200] flex items-center justify-center bg-[#005CA9]/10 backdrop-blur-[1px]">
					<div className="rounded-2xl border-2 border-dashed border-[#005CA9] bg-white px-8 py-6 text-center shadow-xl">
						<FiUploadCloud className="mx-auto h-8 w-8 text-[#005CA9]" aria-hidden />
						<p className="mt-2 text-sm font-semibold text-slate-800">Déposez le fichier pour l'ajouter au stockage</p>
					</div>
				</div>
			)}

			{isManage && (
				<UploadModal
					open={uploadOpen}
					initialFile={droppedFile}
					currentPath={currentPath}
					taxonomies={taxonomies}
					onClose={() => {
						setUploadOpen(false);
						setDroppedFile(null);
					}}
					onUploaded={handleUploaded}
				/>
			)}

			{/* ---- Confirmation : suppression de la sélection ---- */}
			<Modal show={confirmBulkDelete} onHide={() => (bulkBusy ? undefined : setConfirmBulkDelete(false))} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Supprimer la sélection ?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="space-y-2">
						<p className="text-sm text-slate-700">
							<span className="font-semibold">
								{bulkMap.size.toLocaleString("fr-FR")} fichier{bulkMap.size > 1 ? "s" : ""}
							</span>{" "}
							ser{bulkMap.size > 1 ? "ont" : "a"} retiré{bulkMap.size > 1 ? "s" : ""} du stockage et des buckets. Cette action est
							irréversible.
						</p>
						<p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
							Vérifiez l'utilisation des fichiers (vue « Orphelins » ou panneau de détail) : supprimer un fichier encore
							référencé casse son affichage sur le site.
						</p>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setConfirmBulkDelete(false)}
						disabled={bulkBusy}
						className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={runBulkDelete}
						disabled={bulkBusy}
						className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{bulkBusy ? <LoadingSpinner size="xs" inline /> : null} Supprimer définitivement
					</button>
				</Modal.Footer>
			</Modal>

			{/* ---- Confirmation : recompression globale ---- */}
			<Modal show={confirmRecompressAll} onHide={() => setConfirmRecompressAll(false)} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Recompresser toutes les images ?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						Régénère la copie compressée de <span className="font-semibold">toutes</span> les images du stockage. L'opération
						peut prendre plusieurs minutes sur une grosse médiathèque : le résultat s'affiche à la fin.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setConfirmRecompressAll(false)}
						className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={runRecompressAll}
						className="inline-flex items-center gap-1.5 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004a87]"
					>
						<FiZap className="h-4 w-4" /> Lancer la recompression
					</button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};
